<script setup>
import {
  reactive,
  ref,
  computed,
  onMounted,
  onUnmounted,
  watch,
  nextTick,
  useTemplateRef,
} from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, helpers } from '@vuelidate/validators';
import {
  getMessageVariables,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  selectSignature,
  convertMarkdownToHtml,
  findAdditionalSignatureHtml,
} from 'dashboard/helper/editorHelper';
import {
  buildContactableInboxesList,
  prepareNewMessagePayload,
  prepareWhatsAppMessagePayload,
  hasDraftContent as checkDraftContent,
  hasMessageContent,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper.js';
import { useFileUpload } from 'dashboard/composables/useFileUpload';

import ContactSelector from './ContactSelector.vue';
import InboxSelector from './InboxSelector.vue';
import EmailOptions from './EmailOptions.vue';
import MessageEditor from './MessageEditor.vue';
import ActionButtons from './ActionButtons.vue';
import InboxEmptyState from './InboxEmptyState.vue';
import AttachmentsPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ConfirmationModal from '../../../components/widgets/modal/ConfirmationModal.vue';

const props = defineProps({
  contacts: { type: Array, default: () => [] },
  contactId: { type: String, default: null },
  selectedContacts: { type: Array, default: () => [] },
  primaryContactDetails: { type: Object, default: null },
  targetInbox: { type: Object, default: null },
  currentUser: { type: Object, default: null },
  currentAccount: { type: Object, default: null },
  isCreatingContact: { type: Boolean, default: false },
  isFetchingInboxes: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  contactConversationsUiFlags: { type: Object, default: null },
  contactsUiFlags: { type: Object, default: null },
  messageSignature: { type: String, default: '' },
  draftMessage: { type: String, default: '' },
  draftSubject: { type: String, default: '' },
  draftCcEmails: { type: String, default: '' },
  draftBccEmails: { type: String, default: '' },
  draftFiles: { type: Array, default: () => [] },
  uploadingFiles: { type: Array, default: () => [] },
  sourceConversationId: { type: [Number, String], default: null },
});

const emit = defineEmits([
  'searchContacts',
  'resetContactSearch',
  'discard',
  'addContact',
  'removeContact',
  'updateTargetInbox',
  'createConversation',
  'saveDraft',
  'formStateChange',
  'attachFile',
  'removeDraftAttachment',
  'cancelUpload',
  'appendCannedAttachments',
  'overlayToggle',
]);

const showContactsDropdown = ref(false);
const showInboxesDropdown = ref(false);
const showCcEmailsDropdown = ref(false);
const showBccEmailsDropdown = ref(false);
const confirmationModal = ref(null);
const isAIAssistOpen = ref(false);
const isAIModalOpen = ref(false);
const allowSignature = ref(true);
const newMessageContainer = useTemplateRef('newMessageContainer');
let resizeObserver = null;
const maxEditorHeight = ref('30vh');

const allowToggleSignature = computed(() => {
  return props.currentAccount?.allow_toggle_signature ?? true;
});

const toggleSignature = () => {
  allowSignature.value = !allowSignature.value;
};

const calculateMaxHeight = () => {
  const newMessageBox = document.querySelector('.new-message');
  if (newMessageBox && newMessageContainer.value) {
    const containerHeight = window.innerHeight;
    let subtractions = [
      Number(
        getComputedStyle(newMessageBox.parentNode).paddingTop.replace('px', '')
      ),
    ];
    const subtractionsSelectors = [
      '.new-message-footer',
      '.ProseMirror-menubar',
      '.new-message-options',
      '.new-message-header',
      '.html_email_signature',
    ];
    subtractionsSelectors.forEach(selector => {
      const element = document.querySelector(selector);
      if (element) {
        subtractions.push(element.clientHeight);
      }
    });

    const totalSubtractions = subtractions.reduce((acc, curr) => acc + curr, 0);
    const calculatedHeight = Math.min(
      Math.floor((containerHeight - totalSubtractions) * 0.8),
      containerHeight - 300
    );
    maxEditorHeight.value = `${calculatedHeight}px`;

    newMessageContainer.value.style.setProperty(
      '--dynamic-editor-max-height',
      maxEditorHeight.value
    );
  }
};

const setupHeightObserver = (retries = 5) => {
  if (newMessageContainer.value) {
    calculateMaxHeight();

    resizeObserver = new ResizeObserver(() => {
      calculateMaxHeight();
    });
    resizeObserver.observe(newMessageContainer.value);
  } else if (retries > 0) {
    setTimeout(() => setupHeightObserver(retries - 1), 100);
  }
};

const primaryContact = computed(() => props.selectedContacts[0] || null);

const messageVariables = computed(() => {
  const contact = props.primaryContactDetails;
  if (!contact?.id) return {};
  return getMessageVariables({
    conversation: {
      meta: { sender: contact, assignee: props.currentUser },
      id: null,
      custom_attributes: {},
    },
    contact,
    inbox: props.targetInbox,
  });
});

const isCreating = computed(() => props.contactConversationsUiFlags.isCreating);

const initialMarkdownMessage = () => {
  if (props.draftMessage && !props.draftMessage.startsWith('<')) {
    return props.draftMessage;
  }
  return '';
};

const initialHtmlMessage = () => {
  if (props.draftMessage && props.draftMessage.startsWith('<')) {
    return props.draftMessage;
  }
  return null;
};

const initialMessage = () => {
  return initialHtmlMessage() || initialMarkdownMessage();
};

const initialMarkResolved = () => {
  return props.currentAccount?.resolve_new_conversation !== false;
};

const state = reactive({
  message: initialMessage(),
  markdownMessage: initialMarkdownMessage(),
  htmlMessage: initialHtmlMessage(),
  subject: props.draftSubject || '',
  ccEmails: props.draftCcEmails || '',
  bccEmails: props.draftBccEmails || '',
  markResolved: initialMarkResolved(),
});

const isDraftContentPresent = computed(() =>
  checkDraftContent({
    message: state.message,
    subject: state.subject,
    ccEmails: state.ccEmails,
    bccEmails: state.bccEmails,
    files: props.draftFiles,
    uploadingFiles: props.uploadingFiles,
  })
);

const inboxTypes = computed(() => ({
  isEmail: props.targetInbox?.channelType === INBOX_TYPES.EMAIL,
  isTwilio: props.targetInbox?.channelType === INBOX_TYPES.TWILIO,
  isWhatsapp: props.targetInbox?.channelType === INBOX_TYPES.WHATSAPP,
  isWebWidget: props.targetInbox?.channelType === INBOX_TYPES.WEB,
  isApi: props.targetInbox?.channelType === INBOX_TYPES.API,
  isEmailOrWebWidget:
    props.targetInbox === null ||
    props.targetInbox?.channelType === INBOX_TYPES.EMAIL ||
    props.targetInbox?.channelType === INBOX_TYPES.WEB,
  isTwilioSMS:
    (props.targetInbox?.channelType === INBOX_TYPES.TWILIO &&
      props.targetInbox?.medium === 'sms') ||
    props.targetInbox?.channelType === INBOX_TYPES.SMS_BRANA ||
    props.targetInbox?.channelType === INBOX_TYPES.DIDWW,
}));

const isSendDisabled = computed(() => {
  if (isCreating.value) return true;
  if (!props.selectedContacts?.length) return true;
  if (!props.targetInbox) return true;
  if (!inboxTypes.value.isWhatsapp && !hasMessageContent(state.message))
    return true;
  if (inboxTypes.value.isEmail && !state.subject?.trim()) return true;
  return false;
});

const allowMultiple = computed(() => inboxTypes.value.isEmail);

const whatsappMessageTemplates = computed(() =>
  Object.keys(props.targetInbox?.messageTemplates || {}).length
    ? props.targetInbox.messageTemplates
    : []
);

const inboxChannelType = computed(() => {
  const channelType = props.targetInbox?.channelType || '';
  return channelType;
});

const htmlMode = computed(() => {
  return (
    inboxChannelType.value === 'Channel::Email' || inboxChannelType.value === ''
  );
});

const targetSchema = computed(() => {
  return htmlMode.value ? 'html' : 'markdown';
});

const content = computed({
  get() {
    const result =
      targetSchema.value === 'html' ? state.htmlMessage : state.markdownMessage;
    return result || '';
  },
  set(value) {
    if (targetSchema.value === 'html') {
      state.htmlMessage = value;
    } else {
      state.markdownMessage = value;
    }
    state.message = value;
  },
});

const validationRules = computed(() => ({
  selectedContacts: {
    required: value => Array.isArray(value) && value.length > 0,
  },
  targetInbox: { required },
  message: {
    required: helpers.withMessage('Required', value => {
      if (inboxTypes.value.isWhatsapp) return true;
      return hasMessageContent(value);
    }),
  },
  subject: { required: requiredIf(inboxTypes.value.isEmail) },
}));

const v$ = useVuelidate(validationRules, {
  selectedContacts: computed(() => props.selectedContacts),
  targetInbox: computed(() => props.targetInbox),
  message: computed(() => state.message),
  subject: computed(() => state.subject),
});

const validationStates = computed(() => ({
  isContactInvalid:
    v$.value.selectedContacts.$dirty && v$.value.selectedContacts.$invalid,
  isInboxInvalid: v$.value.targetInbox.$dirty && v$.value.targetInbox.$invalid,
  isSubjectInvalid: v$.value.subject.$dirty && v$.value.subject.$invalid,
  isMessageInvalid: v$.value.message.$dirty && v$.value.message.$invalid,
}));

const signatureToApply = computed(() => {
  // The agent explicitly picks the sender alias here, so match the additional
  // signature to it, falling back to the inbox's primary html_signature.
  const additionalSignature = findAdditionalSignatureHtml(
    props.targetInbox?.additionalHtmlSignatures,
    props.targetInbox?.senderEmail
  );
  return selectSignature(
    inboxTypes.value.isEmail || inboxTypes.value.isTwilioSMS,
    additionalSignature || props.targetInbox?.htmlSignature,
    props.messageSignature,
    props.currentUser.available_name,
    props.currentUser.email,
    props.currentUser.position,
    props.currentUser.custom_line,
    props.currentUser.email_signature_picture_url,
    inboxTypes.value.isTwilioSMS ? 'plain' : 'html'
  );
});

const newMessagePayload = ({ isInternal = false } = {}) => {
  let message_content = state.message;

  if (allowSignature.value && signatureToApply.value) {
    message_content += signatureToApply.value;
  }

  const allToEmails = props.selectedContacts
    .map(c => c.email)
    .filter(Boolean)
    .join(',');

  const payloadArgs = {
    targetInbox: props.targetInbox,
    selectedContact: primaryContact.value,
    message: message_content,
    subject: state.subject,
    ccEmails: state.ccEmails,
    bccEmails: state.bccEmails,
    toEmails: allToEmails || undefined,
    currentUser: props.currentUser,
    resolved: state.markResolved,
    isInternal: isInternal,
  };

  // In non-sidebar mode, pass local attachments directly in the payload
  if (!props.isFromSidebar) {
    payloadArgs.attachedFiles = state.attachedFiles;
    payloadArgs.directUploadsEnabled = props.isDirectUploadsEnabled;
  }

  return prepareNewMessagePayload(payloadArgs);
};

const contactableInboxesList = computed(() => {
  return buildContactableInboxesList(primaryContact.value?.contactInboxes);
});

const showNoInboxAlert = computed(() => {
  return (
    primaryContact.value &&
    contactableInboxesList.value.length === 0 &&
    !props.contactsUiFlags.isFetchingInboxes &&
    !props.isFetchingInboxes
  );
});

const isAnyDropdownActive = computed(() => {
  return (
    showContactsDropdown.value ||
    showInboxesDropdown.value ||
    showCcEmailsDropdown.value ||
    showBccEmailsDropdown.value ||
    isAIAssistOpen.value
  );
});

// The AI assist dropdown and its modal own the keyboard while open, so the send shortcuts here and
// the discard-on-Escape in ComposeConversation have to stand down.
const handleAIAssistToggle = isOpen => {
  isAIAssistOpen.value = isOpen;
  emit('overlayToggle', isOpen);
};

// Mirrors ReplyBox.replaceText: the AI returns markdown, so expand any variables and convert it
// when the editor is on the HTML schema.
const replaceText = message => {
  let updatedMessage = replaceVariablesInMessage({
    message,
    variables: messageVariables.value,
  });

  if (targetSchema.value === 'html' && !updatedMessage.startsWith('<')) {
    updatedMessage = convertMarkdownToHtml(updatedMessage);
  }

  content.value = updatedMessage;
};

const handleContactSearch = value => {
  showContactsDropdown.value = true;
  emit('searchContacts', {
    keys: ['email', 'phone_number', 'name'],
    query: value,
  });
};

const handleClearSearch = () => {
  showContactsDropdown.value = false;
  emit('resetContactSearch');
};

const handleDropdownUpdate = (type, value) => {
  if (type === 'cc') {
    showCcEmailsDropdown.value = value;
  } else if (type === 'bcc') {
    showBccEmailsDropdown.value = value;
  } else {
    showContactsDropdown.value = value;
  }
};

const searchCcEmails = value => {
  showCcEmailsDropdown.value = true;
  emit('searchContacts', { keys: ['email'], query: value });
};

const searchBccEmails = value => {
  showBccEmailsDropdown.value = true;
  emit('searchContacts', { keys: ['email'], query: value });
};

const addContact = async ({ value, action, ...rest }) => {
  v$.value.$reset();
  emit('addContact', { value, action, ...rest });
  showContactsDropdown.value = false;
  if (props.selectedContacts.length === 0) {
    showInboxesDropdown.value = true;
  }
};

const removeContact = index => {
  emit('removeContact', index);
};

const handleInboxAction = ({ value, action, ...rest }) => {
  v$.value.$reset();
  emit('updateTargetInbox', { ...rest });
  showInboxesDropdown.value = false;
};

const removeTargetInbox = value => {
  v$.value.$reset();
  emit('updateTargetInbox', value);
};

const onClickInsertEmoji = emoji => {
  content.value += emoji;
};

const onCannedResponseSelected = (meta = {}) => {
  if (
    typeof meta.subject === 'string' &&
    meta.subject.trim() &&
    !(state.subject || '').trim()
  ) {
    state.subject = meta.subject;
  }
  if (Array.isArray(meta.files) && meta.files.length) {
    emit('appendCannedAttachments', meta.files);
  }
};

const handleAttachFile = fileOrFiles => {
  emit('attachFile', fileOrFiles);
};

// File upload composable for handling pasted files
const { onFileUpload } = useFileUpload({
  isATwilioSMSChannel: inboxTypes.value.isTwilioSMS,
  attachFile: ({ blob, file }) => {
    emit('attachFile', { blob, file });
  },
});

// Handle paste events for file attachments
const onPaste = e => {
  const data = e.clipboardData.files;
  if (!data.length || !data[0]) {
    return;
  }

  // For rich editor (email/web widget), only handle non-image files as regular attachments
  // Images are handled directly by the Editor's paste handler
  if (inboxTypes.value.isEmailOrWebWidget) {
    data.forEach(file => {
      // Only non-image files go to regular attachments for rich editor
      if (!file.type.startsWith('image/')) {
        const { name, type, size } = file;
        onFileUpload({ name, type, size, file: file });
      }
    });
  } else {
    // For plain text editor, all files go to regular attachments
    data.forEach(file => {
      const { name, type, size } = file;
      onFileUpload({ name, type, size, file: file });
    });
  }
};

const onDragOver = e => {
  e.preventDefault();
  e.stopPropagation();
};

const onDrop = e => {
  e.preventDefault();
  e.stopPropagation();

  const data = e.dataTransfer.files;
  if (!data.length || !data[0]) {
    return;
  }

  Array.from(data).forEach(file => {
    const { name, type, size } = file;
    onFileUpload({ name, type, size, file: file });
  });
};

const clearForm = () => {
  Object.assign(state, {
    message: '',
    subject: '',
    ccEmails: '',
    bccEmails: '',
    markResolved: initialMarkResolved(),
    attachedFiles: [],
  });
  v$.value.$reset();
};

const handleSendMessage = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  try {
    const success = await emit('createConversation', {
      payload: newMessagePayload(),
      isFromWhatsApp: false,
    });
    if (success) {
      clearForm();
    }
  } catch (error) {
    // Form will not be cleared if conversation creation fails
  }
};

const handleSendInternalMessage = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  try {
    const success = await emit('createConversation', {
      payload: newMessagePayload({ isInternal: true }),
      isFromWhatsApp: false,
    });
    if (success) {
      clearForm();
    }
  } catch (error) {
    // Form will not be cleared if conversation creation fails
  }
};

const handleSendAndResolveOriginal = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  try {
    const success = await emit('createConversation', {
      payload: newMessagePayload(),
      isFromWhatsApp: false,
      resolveSourceConversation: true,
    });
    if (success) {
      clearForm();
    }
  } catch (error) {
    // Form will not be cleared if conversation creation fails
  }
};

const handleSendWhatsappMessage = async ({ message, templateParams }) => {
  const whatsappMessagePayload = prepareWhatsAppMessagePayload({
    targetInbox: props.targetInbox,
    selectedContact: primaryContact.value,
    message,
    templateParams,
    currentUser: props.currentUser,
  });
  await emit('createConversation', {
    payload: whatsappMessagePayload,
    isFromWhatsApp: true,
  });
};

const handleDiscard = async () => {
  // Check if form has any data
  const hasData =
    state.message.replace(/<[^>]*>/g, '').trim() ||
    state.subject.trim() ||
    state.ccEmails.trim() ||
    state.bccEmails.trim() ||
    primaryContact.value ||
    props.targetInbox ||
    props.draftFiles.length > 0;

  // If form has data, show confirmation modal
  if (hasData) {
    const result = await confirmationModal.value.showConfirmation();

    if (!result) return;
  }

  emit('discard');
};

// Watch for form state changes and emit to parent
watch(
  () => state,
  newState => {
    emit('formStateChange', {
      message: newState.message,
      subject: newState.subject,
      ccEmails: newState.ccEmails,
      bccEmails: newState.bccEmails,
    });
  },
  { deep: true, immediate: true }
);

// Watch for draft props changes and apply them to local state
watch(
  () => [
    props.draftMessage,
    props.draftSubject,
    props.draftCcEmails,
    props.draftBccEmails,
  ],
  ([message, subject, ccEmails, bccEmails]) => {
    if (message) {
      state.message = message;
      if (message.startsWith('<')) {
        state.htmlMessage = message;
      } else {
        state.markdownMessage = message;
      }
    }
    if (subject) state.subject = subject;
    if (ccEmails) state.ccEmails = ccEmails;
    if (bccEmails) state.bccEmails = bccEmails;
  }
);

const handleWindowResize = () => {
  calculateMaxHeight();
};

onMounted(async () => {
  document.addEventListener('paste', onPaste);
  document.addEventListener('dragover', onDragOver);
  document.addEventListener('drop', onDrop);
  window.addEventListener('resize', handleWindowResize);

  nextTick(() => setupHeightObserver());
});

onUnmounted(() => {
  document.removeEventListener('paste', onPaste);
  document.removeEventListener('dragover', onDragOver);
  document.removeEventListener('drop', onDrop);
  window.removeEventListener('resize', handleWindowResize);

  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});
</script>

<template>
  <div
    ref="newMessageContainer"
    class="new-message w-[70%] max-md:w-full max-md:h-full max-md:rounded-none divide-y overflow-visible transition-all duration-300 ease-in-out top-full justify-between flex flex-col bg-n-alpha-3 border backdrop-blur-[100px] rounded-xl"
    :class="
      isAIModalOpen
        ? 'border-transparent divide-transparent shadow-none opacity-40'
        : 'border-n-strong divide-n-strong shadow-sm'
    "
  >
    <div
      class="new-message-header flex items-center justify-between px-4 py-3 border-b"
      :class="isAIModalOpen ? 'border-transparent' : 'border-n-strong'"
    >
      <h3 class="text-sm font-medium text-n-foreground">
        {{ $t('COMPOSE_NEW_CONVERSATION.TITLE') }}
      </h3>
      <button
        v-tooltip.top-end="$t('COMPOSE_NEW_CONVERSATION.FORM.DISCARD')"
        type="button"
        class="flex items-center justify-center w-8 h-8 rounded-md hover:bg-n-alpha-8 transition-colors duration-200"
        @click="handleDiscard"
      >
        <svg
          class="w-4 h-4 text-n-muted"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>

    <div class="new-message-options">
      <ContactSelector
        :contacts="contacts"
        :selected-contacts="selectedContacts"
        :allow-multiple="allowMultiple"
        :show-contacts-dropdown="showContactsDropdown"
        :is-loading="isLoading"
        :is-creating-contact="isCreatingContact"
        :contact-id="contactId"
        :contactable-inboxes-list="contactableInboxesList"
        :show-inboxes-dropdown="showInboxesDropdown"
        :has-errors="validationStates.isContactInvalid"
        @search-contacts="handleContactSearch"
        @clear-search="handleClearSearch"
        @add-contact="addContact"
        @remove-contact="removeContact"
        @update-dropdown="handleDropdownUpdate"
      />
      <InboxEmptyState v-if="showNoInboxAlert" />
      <InboxSelector
        v-else
        :target-inbox="targetInbox"
        :selected-contact="primaryContact"
        :show-inboxes-dropdown="showInboxesDropdown"
        :contactable-inboxes-list="contactableInboxesList"
        :has-errors="validationStates.isInboxInvalid"
        @update-inbox="removeTargetInbox"
        @toggle-dropdown="showInboxesDropdown = $event"
        @handle-inbox-action="handleInboxAction"
      />

      <EmailOptions
        v-if="inboxTypes.isEmail"
        v-model:cc-emails="state.ccEmails"
        v-model:bcc-emails="state.bccEmails"
        v-model:subject="state.subject"
        :contacts="contacts"
        :show-cc-emails-dropdown="showCcEmailsDropdown"
        :show-bcc-emails-dropdown="showBccEmailsDropdown"
        :is-loading="isLoading"
        :has-errors="validationStates.isSubjectInvalid"
        @search-cc-emails="searchCcEmails"
        @search-bcc-emails="searchBccEmails"
        @update-dropdown="handleDropdownUpdate"
      />
    </div>

    <MessageEditor
      v-if="!inboxTypes.isWhatsapp && !showNoInboxAlert"
      v-model="content"
      :target-schema="targetSchema"
      :message-signature="signatureToApply"
      :allow-signature="allowSignature"
      :allow-toggle-signature="allowToggleSignature"
      :is-email-or-web-widget-inbox="inboxTypes.isEmailOrWebWidget"
      :has-errors="validationStates.isMessageInvalid"
      :has-attachments="draftFiles.length > 0 || uploadingFiles.length > 0"
      :channel-type="inboxChannelType"
      :inbox-id="targetInbox?.id"
      :show-image-resize-toolbar="htmlMode"
      :variables="messageVariables"
      @canned-response-selected="onCannedResponseSelected"
    />

    <div class="new-message-footer">
      <AttachmentsPreview
        v-if="draftFiles.length > 0 || uploadingFiles.length > 0"
        :draft-files="draftFiles"
        :uploading-files="uploadingFiles"
        @remove-draft-attachment="emit('removeDraftAttachment', $event)"
        @cancel-upload="emit('cancelUpload', $event)"
      />

      <ActionButtons
        v-model:mark-resolved="state.markResolved"
        :is-whatsapp-inbox="inboxTypes.isWhatsapp"
        :is-email-or-web-widget-inbox="inboxTypes.isEmailOrWebWidget"
        :is-twilio-sms-inbox="inboxTypes.isTwilioSMS"
        :message-templates="whatsappMessageTemplates"
        :channel-type="inboxChannelType"
        :is-loading="isCreating"
        :disable-send-button="isSendDisabled"
        :has-selected-inbox="!!targetInbox"
        :has-no-inbox="showNoInboxAlert"
        :is-dropdown-active="isAnyDropdownActive"
        :message-signature="allowToggleSignature ? signatureToApply : false"
        :disable-save-draft="!isDraftContentPresent"
        :allow-signature="allowSignature"
        :can-resolve-source-conversation="!!sourceConversationId"
        :message="content"
        @replace-text="replaceText"
        @ai-assist-toggle="handleAIAssistToggle"
        @ai-modal-toggle="isAIModalOpen = $event"
        @insert-emoji="onClickInsertEmoji"
        @attach-file="handleAttachFile"
        @send-message="handleSendMessage"
        @send-whatsapp-message="handleSendWhatsappMessage"
        @save-draft="$emit('saveDraft')"
        @toggle-signature="toggleSignature"
        @send-as-internal="handleSendInternalMessage"
        @send-and-resolve-original="handleSendAndResolveOriginal"
      />
    </div>
  </div>

  <ConfirmationModal
    ref="confirmationModal"
    :title="$t('COMPOSE_NEW_CONVERSATION.FORM.DISCARD_MODAL.TITLE')"
    :description="$t('COMPOSE_NEW_CONVERSATION.FORM.DISCARD_MODAL.DESCRIPTION')"
    :confirm-label="$t('COMPOSE_NEW_CONVERSATION.FORM.DISCARD_MODAL.CONFIRM')"
    :cancel-label="$t('COMPOSE_NEW_CONVERSATION.FORM.DISCARD_MODAL.CANCEL')"
  />
</template>
